import type {
  TotalOrderInfoQuery,
  CheckAndRecordShoppingPopupMutation,
  CallerAddressesQuery,
  CallerAddressInputType,
  EnableAutoReloadMutationVariables,
  CallerActiveHoursQuery,
  CallerDateEventsQuery,
  GetVisitorInformationQuery,
  SaveGroceryPopupResponseMutationVariables,
  CallerFragmentFragment,
  CallerInputType,
} from '@/gql/graphql';

import type { CustomLocationType } from '@/models/address';

export type Caller = Extract<CallerFragmentFragment, {}> & {
  customAddresses?: CustomLocationType[];
  dobMonth?: number | null;
  dobDay?: number | null;
  dobYear?: number | null;
};
export type CallerQueryInput = {
  phoneNumber?: string;
  partnerId?: number;
};
export type TotalOrderInfo = Extract<TotalOrderInfoQuery['totalOrderInfo'], {}>;
export type ShoppingPlace = Extract<CheckAndRecordShoppingPopupMutation['checkAndRecordShoppingPopup'], {}>[0];
export type PaginatedCallerAddressTypeResponse = Extract<CallerAddressesQuery['callerAddresses'], {}>;
export type CallerAddress = Extract<PaginatedCallerAddressTypeResponse['rows'], {}>[number];
export type CallerAddressInput = CallerAddressInputType;
export type EnableAutoReloadInput = EnableAutoReloadMutationVariables & {
  autoReloadCreditsEnabled?: boolean;
};
export type VisitorInformation = Extract<GetVisitorInformationQuery['getVisitorInformation'], {}>[number];

export type CallerActiveHours = Extract<CallerActiveHoursQuery['callerActiveHours'], {}>;
export type CallerDateEvent = Extract<CallerDateEventsQuery['callerDateEvents'], {}>;

export type CallerInput = CallerInputType & {
  dobMonth?: number;
  dobDay?: number;
  dobYear?: number;
};
export type CallerProperty = keyof Caller;

export type SavePopupResponseInput = SaveGroceryPopupResponseMutationVariables;

export interface CallerAdditionalInfo {
  isVisuallyImpaired: boolean;
  isCane: boolean;
  isElbowAssist: boolean;
  isTraveling: boolean;
  isWalker: boolean;
  isWheelchair: boolean;
  isPowerWheelchairScooter: boolean;
  isHardOfHearing: boolean;
  isServiceDog: boolean;
  isOxygenTank: boolean;
  isPoorMemory: boolean;
  isNoBigCars: boolean;
  isNoSmallCars: boolean;
  isVerballyImpaired: boolean;
  isSlowWalker: boolean;
  isAda: boolean;
  noAdditionalInfo: boolean;
  isSpeech: boolean;
}

export const CallerAdditionalInfoProps = [
  'noAdditionalInfo',
  'isVisuallyImpaired',
  'isCane',
  'isElbowAssist',
  'isTraveling',
  'isWalker',
  'isWheelchair',
  'isHardOfHearing',
  'isServiceDog',
  'isOxygenTank',
  'isPoorMemory',
  'isNoBigCars',
  'isNoSmallCars',
  'isVerballyImpaired',
  'isSpeech',
  'isSlowWalker',
  'isPowerWheelchairScooter',
  'isAda',
] as const satisfies CallerProperty[];

export const CallerProperties = [
  'id',
  'prefix',
  'lastName',
  'firstName',
  'language1',
  'language2',
  'isNeedHelp',
  'dob',
  'phoneNumber',
  'homeNumber',
  'registerPlan',
  'stripeCustomerId',
  'email',
  'homeNote',
  'streetAddress',
  'streetAddress2',
  'city',
  'state',
  'zip',
  'country',
  ...CallerAdditionalInfoProps,
] as const satisfies CallerProperty[];

export enum CompleteRegistrationSteps {
  CustomerInformation = 0,
  FamilyMemberInformation = 1,
  ChooseMembership = 2,
  Payment = 3,
  Completed = 4,
}

export enum RideRegistrationStepKey {
  ServiceCheck = 'serviceCheck',
  CustomerInformation = 'customerInformation',
  ChooseMembership = 'chooseMembership',
  BillingInformation = 'billingInformation',
  FirstRideSetup = 'firstRideSetup',
  PullupCustomerInfo = 'pullupCustomerInfo',
}

export type RideRegistrationStep = {
  step: number;
  title: string;
  key: RideRegistrationStepKey;
};

export const rideRegistrationSteps = [
  { step: 0, title: 'Service Check', key: RideRegistrationStepKey.ServiceCheck },
  { step: 1, title: 'Customer & Family Information', key: RideRegistrationStepKey.CustomerInformation },
  { step: 2, title: 'Choose Membership', key: RideRegistrationStepKey.ChooseMembership },
  { step: 3, title: 'Billing Information', key: RideRegistrationStepKey.BillingInformation },
  { step: 4, title: 'First Ride Setup', key: RideRegistrationStepKey.FirstRideSetup },
] as const satisfies readonly RideRegistrationStep[];

export const plansBeforeSoftRegRideRegistrationSteps = [
  { step: 0, title: 'Service Check', key: RideRegistrationStepKey.ServiceCheck },
  { step: 1, title: 'Choose Membership', key: RideRegistrationStepKey.ChooseMembership },
  { step: 2, title: 'Customer & Family Information', key: RideRegistrationStepKey.CustomerInformation },
  { step: 3, title: 'Billing Information', key: RideRegistrationStepKey.BillingInformation },
  { step: 4, title: 'First Ride Setup', key: RideRegistrationStepKey.FirstRideSetup },
] as const satisfies readonly RideRegistrationStep[];

export enum FlaggedUserType {
  NotFlagged = 0,
  FraudUser = 1,
  NeverFraudAgain = 2,
  NeedsToClearDisputes = 3,
}
